/**
 * soundscape — the pure phase→sound mapping for the Stage/host ambient engine.
 *
 * Split out of useNightAmbient so both the game (React hook) and the /studio
 * tuning UI can drive the exact same behavior. `planForPhase` is pure: it reads
 * the live tuning snapshot (gains + per-phase choreo) plus the audio manifest and
 * returns a declarative plan. `applyPhaseSoundscape` executes that plan against a
 * StagePlayer.
 *
 * Beats (background NEVER stops during the night — one-shots layer over it):
 *  - NIGHT_INTRO   → start random rain bed (loop) + random texture layer (loop),
 *                    low gains so narration sits on top; hold narration briefly.
 *  - NIGHT_WOLVES  → one-shot wolves-wake stinger over the bed.
 *  - NIGHT_INTERLUDE → one-shot wolves-go-to-sleep stinger under the interlude.
 *  - NIGHT_SEER    → one-shot seer stinger over the bed.
 *  - RESOLVE_NIGHT → leave the bed running into DAWN.
 *  - DAWN          → stop night ambient (fade), play the wake one-shot.
 *  - DISCUSS/VOTE  → quiet generic day-ambient loop.
 *  - LOBBY         → loop the lobby music.
 *  - DEAL / END    → fade everything out.
 *
 * Ducking, stinger delays, and narration holds all live in tuning.json (see
 * audioTuning.ts) and are applied by stagePlayer, not here.
 */
import type { Phase } from '@ringmaster/shared';
import type { StagePlayer, AmbientLayer } from './stagePlayer.js';
import { stagePlayer } from './stagePlayer.js';
import { getTuning } from './audioTuning.js';
import {
  NIGHT_RAIN_BEDS,
  NIGHT_LAYERS,
  SFX,
  DAY_AMBIENT,
  LOBBY_MUSIC,
} from '../assets/audioManifest.js';

export interface PhaseSoundscapePlan {
  /** Looping layers to start (replaces current set), or 'stop' to fade out, or undefined = leave as-is. */
  ambient?: AmbientLayer[] | 'stop';
  /** Fade for the 'stop' case (ms). */
  stopFadeMs?: number;
  /** One-shot stinger to fire on entry. */
  stinger?: { url: string; delayMs: number; gain: number };
  /** How long to hold the narration queue after phase entry (ms). */
  narrationDelayMs?: number;
}

/**
 * Compute the soundscape plan for entering `phase`. Pure — reads the live tuning
 * snapshot (so studio slider edits take effect on the next phase entry) and the
 * manifest, returns a declarative plan without touching any player.
 */
export function planForPhase(phase: Phase): PhaseSoundscapePlan {
  const { gains, fades, choreo } = getTuning();

  switch (phase) {
    case 'NIGHT_INTRO': {
      // Start the looping night bed + one texture layer.
      return {
        ambient: [
          { url: pick(NIGHT_RAIN_BEDS), gain: gains.bed, role: 'bed' },
          { url: pick(NIGHT_LAYERS), gain: gains.layer, role: 'layer' },
        ],
        narrationDelayMs: choreo.NIGHT_INTRO?.narrationDelayMs,
      };
    }
    case 'NIGHT_WOLVES': {
      // Wolves wake — stinger over the (still-running) bed.
      return {
        stinger: { url: SFX.wolves, delayMs: choreo.NIGHT_WOLVES?.stingerDelayMs ?? 0, gain: gains.sfx },
        narrationDelayMs: choreo.NIGHT_WOLVES?.narrationDelayMs,
      };
    }
    case 'NIGHT_INTERLUDE': {
      // Wolves go to sleep — the go-to-sleep stinger over the (still-running)
      // bed, under the WOLVES_SLEEP narration.
      return {
        stinger: {
          url: SFX.wolvesSleep,
          delayMs: choreo.NIGHT_INTERLUDE?.stingerDelayMs ?? 0,
          gain: gains.sfx,
        },
        narrationDelayMs: choreo.NIGHT_INTERLUDE?.narrationDelayMs,
      };
    }
    case 'NIGHT_SEER': {
      // Seer wakes — stinger over the bed.
      return {
        stinger: { url: SFX.seer, delayMs: choreo.NIGHT_SEER?.stingerDelayMs ?? 0, gain: gains.sfx },
        narrationDelayMs: choreo.NIGHT_SEER?.narrationDelayMs,
      };
    }
    case 'RESOLVE_NIGHT': {
      // Transient — keep the bed running into DAWN.
      return {};
    }
    case 'DAWN': {
      // Night is over — fade out the bed, play the wake one-shot.
      return {
        ambient: 'stop',
        stopFadeMs: fades.dawnStopMs,
        stinger: { url: SFX.wake, delayMs: choreo.DAWN?.stingerDelayMs ?? 0, gain: gains.sfx },
        narrationDelayMs: choreo.DAWN?.narrationDelayMs,
      };
    }
    case 'DISCUSS':
    case 'VOTE':
    case 'RESOLVE_VOTE': {
      // Quiet day ambient loop under discussion / voting.
      return { ambient: [{ url: DAY_AMBIENT, gain: gains.day, role: 'day' }] };
    }
    case 'LOBBY': {
      // Players are gathering — loop the lobby music (no-op until the host's
      // create/start gesture has unlocked audio). Returning to the lobby via
      // Play Again brings it back too.
      return { ambient: [{ url: LOBBY_MUSIC, gain: gains.lobby, role: 'lobby' }] };
    }
    case 'DEAL':
    case 'END': {
      // No ambient outside the round loop (lobby music stops as the deal begins;
      // the night loop takes over on NIGHT_INTRO).
      return { ambient: 'stop', stopFadeMs: fades.ambientStopMs };
    }
    // ── The Whisper — arcana séance soundscape. The Seer's stinger opens each
    //    round's ritual; the day-ambient bed carries the interrogation + hunt so
    //    the room hums under the narration. No new assets (reuses SFX.seer +
    //    DAY_AMBIENT); richer beds land with the game's own audio later.
    case 'WHISPER_INTRO': {
      // The Seer wakes to speak her word — her stinger over a quiet bed.
      return {
        ambient: [{ url: DAY_AMBIENT, gain: gains.day, role: 'day' }],
        stinger: { url: SFX.seer, delayMs: choreo.NIGHT_SEER?.stingerDelayMs ?? 0, gain: gains.sfx },
        narrationDelayMs: choreo.NIGHT_SEER?.narrationDelayMs,
      };
    }
    case 'QUESTION':
    case 'RESOLVE_GUESS':
    case 'HUNT':
    case 'RESOLVE_HUNT':
      // Keep the quiet séance bed running under the interrogation / hunt.
      return { ambient: [{ url: DAY_AMBIENT, gain: gains.day, role: 'day' }] };
    case 'SCORE':
      // Standings — let the bed keep humming (no fresh directive).
      return {};
    // ── One Night — reuses Blood Moon's night bed + wake stingers (no dedicated
    // One Night audio yet). One night, one merged wake stinger for the parallel
    // Werewolf/Seer/Thief beat, then one silent swap beat (Joker), then
    // dawn/discuss/vote like normal.
    // TODO(onenight): a dedicated stinger for the merged beat / Joker if the
    // game gets bespoke audio.
    case 'ONENIGHT_NIGHTFALL': {
      return {
        ambient: [
          { url: pick(NIGHT_RAIN_BEDS), gain: gains.bed, role: 'bed' },
          { url: pick(NIGHT_LAYERS), gain: gains.layer, role: 'layer' },
        ],
        narrationDelayMs: choreo.NIGHT_INTRO?.narrationDelayMs,
      };
    }
    case 'ONENIGHT_NIGHT_ACTIONS': {
      // Werewolf, Seer and Thief all wake together now — one stinger for the
      // merged beat (reuses the wolf-wake cue; no dedicated "everyone at once"
      // sound yet).
      return {
        stinger: { url: SFX.wolves, delayMs: choreo.NIGHT_WOLVES?.stingerDelayMs ?? 0, gain: gains.sfx },
        narrationDelayMs: choreo.NIGHT_WOLVES?.narrationDelayMs,
      };
    }
    case 'ONENIGHT_DAWN': {
      return {
        ambient: 'stop',
        stopFadeMs: fades.dawnStopMs,
        stinger: { url: SFX.wake, delayMs: choreo.DAWN?.stingerDelayMs ?? 0, gain: gains.sfx },
        narrationDelayMs: choreo.DAWN?.narrationDelayMs,
      };
    }
    case 'ONENIGHT_DISCUSS':
    case 'ONENIGHT_VOTE':
    case 'ONENIGHT_RESOLVE':
      return { ambient: [{ url: DAY_AMBIENT, gain: gains.day, role: 'day' }] };
    default: {
      const _exhaustive: never = phase;
      void _exhaustive;
      return {};
    }
  }
}

/**
 * Execute the plan for `phase` against a player (defaults to the singleton
 * stagePlayer). Applies ambient swaps/stops, fires the stinger, and installs a
 * narration hold so the stinger lands before the narrator speaks.
 */
export function applyPhaseSoundscape(phase: Phase, player: StagePlayer = stagePlayer): void {
  const plan = planForPhase(phase);

  if (plan.ambient === 'stop') {
    player.stopAmbient(plan.stopFadeMs);
  } else if (plan.ambient) {
    player.setAmbient(plan.ambient);
  }

  if (plan.stinger) {
    player.playOneShot(plan.stinger.url, plan.stinger.gain, plan.stinger.delayMs);
  }

  if (plan.narrationDelayMs) {
    player.holdNarrationUntil(Date.now() + plan.narrationDelayMs);
  }
}

/**
 * Every mp3 the soundscape can play (all beds, layers, SFX, day, lobby) so
 * callers can warm the decode cache once at unlock: `player.preload(preloadUrls())`.
 */
export function preloadUrls(): string[] {
  return [
    ...NIGHT_RAIN_BEDS,
    ...NIGHT_LAYERS,
    ...Object.values(SFX),
    DAY_AMBIENT,
    LOBBY_MUSIC,
  ];
}

/** Uniform random pick (ambient variety — determinism not needed for audio). */
function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]!;
}
